import React, { useState, useEffect } from 'react';
import NotificationBell from '../common/NotificationBell';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
  Badge
} from '@mui/material';
import {
  AccountCircle,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Event as EventIcon,
  Assessment as ReportsIcon,
  Note as NotesIcon,
  AdminPanelSettings as AdminIcon,
  Logout as LogoutIcon,
  VpnKey as PasswordIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  SupervisorAccount as SupervisorIcon,
  History as ActivityIcon,
  Group as ClientsIcon,
  AssignmentTurnedIn as TriageIcon,
  Notifications as NotificationsIcon,
  SwapHoriz as SwitchIcon,
  PersonOutline as TherapistIcon,
  FindInPage as ReviewIcon,
  Science as ResearcherIcon,
  Gavel as QaIcon,
  School as SimulationsIcon
} from '@mui/icons-material';
import * as clinicalSupervisorApi from '../../services/clinicalSupervisorApi';

const ClinicalSupervisorHeader = ({
  user,
  onLogout,
  onDeleteAccount,
  onChangePassword,
  pageTitle
}) => {
  const location = useLocation();
  const theme = useTheme();
  // Use higher breakpoints due to more nav items (9 items vs therapist's 6)
  const isMobile = useMediaQuery(theme.breakpoints.down('lg')); // Show hamburger menu below 1200px
  const isTablet = useMediaQuery(theme.breakpoints.down('xl')); // Use xl (1536px) for tablet compact view

  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(0);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleDrawer = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  // Check if user has other roles
  const hasAdminRole = user?.role && (
    (Array.isArray(user.role) && user.role.includes('admin')) ||
    user.role === 'admin'
  );

  const hasTherapistRole = user?.role && (
    (Array.isArray(user.role) && user.role.includes('therapist')) ||
    user.role === 'therapist'
  );

  const hasResearcherRole = user?.role && (
    (Array.isArray(user.role) && user.role.includes('researcher')) ||
    user.role === 'researcher'
  );

  // Determine active tab based on current route
  const getActiveTab = () => {
    const path = location.pathname;

    if (path.includes('/clinical-supervisor/therapists')) return 'therapists';
    if (path.includes('/clinical-supervisor/clients')) return 'clients';
    if (path.includes('/clinical-supervisor/triage')) return 'triage';
    if (path.includes('/clinical-supervisor/care-notes')) return 'care-notes';
    if (path.includes('/clinical-supervisor/reports')) return 'reports';
    if (path.includes('/clinical-supervisor/activity')) return 'activity';
    if (path.includes('/clinical-supervisor/dashboard')) return 'dashboard';
    return '';
  };

  const activeTab = getActiveTab();

  // Fetch alert counts on mount and periodically
  useEffect(() => {
    const fetchAlertCount = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const data = await clinicalSupervisorApi.getDashboard(token);
        if (data.alertStats) {
          // Only count pending and critical/urgent alerts for badge
          const urgentCount = (data.alertStats.criticalCount || 0) + (data.alertStats.urgentCount || 0);
          setAlertCount(urgentCount);
        }
      } catch (error) {
        console.error('Error fetching alert count:', error);
      }
    };

    fetchAlertCount();

    // Refresh every 60 seconds
    const interval = setInterval(fetchAlertCount, 60000);

    return () => clearInterval(interval);
  }, []);

  // Navigation items configuration
  const navItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/clinical-supervisor/dashboard'
    },
    {
      id: 'therapists',
      label: 'My Therapists',
      icon: <PeopleIcon />,
      path: '/clinical-supervisor/therapists'
    },
    {
      id: 'schedules',
      label: 'Schedules',
      icon: <EventIcon />,
      path: '/clinical-supervisor/schedules'
    },
    {
      id: 'clients',
      label: 'Client Management',
      icon: <ClientsIcon />,
      path: '/clinical-supervisor/clients'
    },
    {
      id: 'activity',
      label: 'Activity Log',
      icon: <ActivityIcon />,
      path: '/clinical-supervisor/activity'
    },
    {
      id: 'triage',
      label: 'Triage Reviews',
      icon: <TriageIcon />,
      path: '/clinical-supervisor/triage',
      badge: 0 // TODO: Could be populated from triage API
    },
    {
      id: 'care-notes',
      label: 'Care Note Reviews',
      icon: <NotesIcon />,
      path: '/clinical-supervisor/care-notes/pending',
      badge: 0 // TODO: Could be populated from care notes API
    },
    {
      id: 'alerts',
      label: 'Team Alerts',
      icon: <NotificationsIcon />,
      path: '/clinical-supervisor/alerts',
      badge: alertCount // Dynamically populated from dashboard API
    },
    {
      id: 'reviews',
      label: 'Client Reviews',
      icon: <ReviewIcon />,
      path: '/clinical-supervisor/reviews'
    },
    {
      id: 'qa',
      label: 'QA Rubric Review',
      icon: <QaIcon />,
      path: '/clinical-supervisor/qa'
    },
    {
      id: 'simulations',
      label: 'Simulations',
      icon: <SimulationsIcon />,
      path: '/clinical-supervisor/simulations'
    },
    {
      id: 'practice-sessions',
      label: 'Practice Sessions',
      icon: <ReviewIcon />,
      path: '/clinical-supervisor/practice-sessions'
    },
    {
      id: 'reports',
      label: 'Reports & Analytics',
      icon: <ReportsIcon />,
      path: '/clinical-supervisor/reports'
    }
  ];

  // Mobile/Tablet Drawer Content
  const DrawerContent = () => (
    <Box sx={{ width: 280, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Drawer Header */}
      <Box sx={{
        p: 2,
        backgroundColor: '#2e7d32',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box>
          <Typography variant="h6">Navigation</Typography>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            {user?.email}
          </Typography>
        </Box>
        <IconButton
          onClick={toggleDrawer}
          sx={{ color: 'white' }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* User Profile Section */}
      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 48, height: 48, backgroundColor: '#2e7d32' }}>
            <SupervisorIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2">Clinical Supervisor</Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Navigation Items */}
      <List sx={{ flex: 1, pt: 2 }}>
        {navItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton
              component={RouterLink}
              to={item.path}
              selected={activeTab === item.id}
              onClick={toggleDrawer}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'rgba(46, 125, 50, 0.08)',
                  borderLeft: '3px solid #2e7d32',
                  '&:hover': {
                    backgroundColor: 'rgba(46, 125, 50, 0.12)'
                  }
                }
              }}
            >
              <ListItemIcon sx={{ color: activeTab === item.id ? '#2e7d32' : 'inherit' }}>
                {item.badge ? (
                  <Badge badgeContent={item.badge} color="error">
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Role Switching Section */}
      {(hasTherapistRole || hasAdminRole || hasResearcherRole) && (
        <>
          <Divider />
          <List sx={{ py: 1 }}>
            <ListItem disablePadding>
              <ListItemButton disabled>
                <ListItemText
                  primary="Switch View"
                  primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                />
              </ListItemButton>
            </ListItem>
            {hasTherapistRole && (
              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/therapist/dashboard"
                  onClick={toggleDrawer}
                >
                  <ListItemIcon>
                    <TherapistIcon />
                  </ListItemIcon>
                  <ListItemText primary="Therapist View" />
                </ListItemButton>
              </ListItem>
            )}
            {hasResearcherRole && (
              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/researcher/dashboard"
                  onClick={toggleDrawer}
                >
                  <ListItemIcon>
                    <ResearcherIcon />
                  </ListItemIcon>
                  <ListItemText primary="Researcher View" />
                </ListItemButton>
              </ListItem>
            )}
            {hasAdminRole && (
              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/admin/dashboard"
                  onClick={toggleDrawer}
                >
                  <ListItemIcon>
                    <AdminIcon />
                  </ListItemIcon>
                  <ListItemText primary="Admin View" />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </>
      )}

      {/* Account Actions */}
      <Divider />
      <List sx={{ pb: 1 }}>
        <ListItem disablePadding>
          <ListItemButton onClick={() => { onChangePassword(); toggleDrawer(); }}>
            <ListItemIcon>
              <PasswordIcon />
            </ListItemIcon>
            <ListItemText primary="Change Password" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => { onLogout(); toggleDrawer(); }}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => { onDeleteAccount(); toggleDrawer(); }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon sx={{ color: 'error.main' }}>
              <DeleteIcon />
            </ListItemIcon>
            <ListItemText primary="Delete Account" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: '#2e7d32',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <Toolbar sx={{ minHeight: '64px', px: { xs: 1, sm: 2 } }}>
          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={toggleDrawer}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo */}
          <RouterLink
            to="/clinical-supervisor/dashboard"
            style={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              marginRight: isMobile ? 'auto' : '32px',
              flexShrink: 0
            }}
          >
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <img
                src={`${process.env.PUBLIC_URL}/logo-desktop.png`}
                srcSet={`${process.env.PUBLIC_URL}/logo-desktop.png 1x, ${process.env.PUBLIC_URL}/logo-desktop2x.png 2x`}
                alt="AIMHI Logo"
                style={{
                  height: '40px',
                  width: 'auto'
                }}
              />
            </Box>
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
              <img
                src={`${process.env.PUBLIC_URL}/logo-mobile.png`}
                srcSet={`${process.env.PUBLIC_URL}/logo-mobile.png 1x, ${process.env.PUBLIC_URL}/logo-mobile2x.png 2x`}
                alt="AIMHI Logo"
                style={{
                  height: '35px',
                  width: 'auto'
                }}
              />
            </Box>
          </RouterLink>

          {/* Page Title - Desktop/Tablet only */}
          {!isMobile && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              mr: 3,
              minWidth: isTablet ? '150px' : '200px'
            }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.75rem',
                  lineHeight: 1,
                  mb: 0.25,
                  display: { xs: 'none', lg: 'block' }
                }}
              >
                Clinical Supervisor:
              </Typography>
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontSize: isTablet ? '1rem' : '1.1rem',
                  fontWeight: 500,
                  lineHeight: 1.2
                }}
              >
                {pageTitle}
              </Typography>
            </Box>
          )}

          {/* Desktop Navigation - Show only on larger screens */}
          {!isTablet && (
            <Box sx={{ flexGrow: 1, display: 'flex', gap: 0.5, alignItems: 'center' }}>
              {navItems.map((item) => (
                <Button
                  key={item.id}
                  color="inherit"
                  component={RouterLink}
                  to={item.path}
                  startIcon={item.badge ? (
                    <Badge badgeContent={item.badge} color="error">
                      {item.icon}
                    </Badge>
                  ) : null}
                  sx={{
                    textTransform: 'uppercase',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    letterSpacing: 0.5,
                    px: 1.5,
                    py: 1,
                    minWidth: 'auto',
                    position: 'relative',
                    '&:after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      backgroundColor: '#81c784',
                      display: activeTab === item.id ? 'block' : 'none'
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {/* Tablet Navigation - Compact Pills */}
          {isTablet && !isMobile && (
            <Box sx={{ flexGrow: 1, display: 'flex', gap: 0.5, alignItems: 'center' }}>
              {navItems.slice(0, 3).map((item) => (
                <Chip
                  key={item.id}
                  label={item.label.split(' ')[0]} // Show only first word on tablet
                  component={RouterLink}
                  to={item.path}
                  clickable
                  icon={item.icon}
                  sx={{
                    backgroundColor: activeTab === item.id ? 'rgba(129, 199, 132, 0.3)' : 'transparent',
                    color: 'white',
                    borderColor: activeTab === item.id ? '#81c784' : 'transparent',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)'
                    }
                  }}
                />
              ))}
              {/* More button for remaining items */}
              <IconButton
                color="inherit"
                onClick={toggleDrawer}
                size="small"
                sx={{ ml: 1 }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}

          {/* Spacer */}
          {(isMobile || isTablet) && <Box sx={{ flexGrow: 1 }} />}

          <NotificationBell />

          {/* User Info - Desktop only */}
          {!isTablet && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              mr: 1,
              ml: 2
            }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.75rem',
                  lineHeight: 1.2
                }}
              >
                Logged in as:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'inherit',
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}
              >
                {user?.email}
              </Typography>
            </Box>
          )}

          {/* Account Menu Button */}
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
            data-testid="user-menu-button"
          >
            <AccountCircle />
          </IconButton>

          {/* Account Menu Dropdown */}
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {/* Show email in menu for mobile/tablet */}
            {isTablet && (
              <>
                <MenuItem disabled>
                  <Typography variant="caption">{user?.email}</Typography>
                </MenuItem>
                <Divider />
              </>
            )}
            <MenuItem onClick={() => { handleClose(); onChangePassword(); }}>
              <ListItemIcon>
                <PasswordIcon fontSize="small" />
              </ListItemIcon>
              Change Password
            </MenuItem>

            {/* Role Switching Options */}
            {(hasTherapistRole || hasAdminRole || hasResearcherRole) && (
              <>
                <Divider />
                {hasTherapistRole && (
                  <MenuItem
                    component={RouterLink}
                    to="/therapist/dashboard"
                    onClick={handleClose}
                  >
                    <ListItemIcon>
                      <SwitchIcon fontSize="small" />
                    </ListItemIcon>
                    Switch to Therapist View
                  </MenuItem>
                )}
                {hasResearcherRole && (
                  <MenuItem
                    component={RouterLink}
                    to="/researcher/dashboard"
                    onClick={handleClose}
                  >
                    <ListItemIcon>
                      <SwitchIcon fontSize="small" />
                    </ListItemIcon>
                    Switch to Researcher View
                  </MenuItem>
                )}
                {hasAdminRole && (
                  <MenuItem
                    component={RouterLink}
                    to="/admin/dashboard"
                    onClick={handleClose}
                  >
                    <ListItemIcon>
                      <SwitchIcon fontSize="small" />
                    </ListItemIcon>
                    Switch to Admin View
                  </MenuItem>
                )}
              </>
            )}

            <Divider />
            <MenuItem onClick={() => { onLogout(); handleClose(); }} data-testid="logout-button">
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => { onDeleteAccount(); handleClose(); }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              Delete Account
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Mobile/Tablet Navigation Drawer */}
      <Drawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={toggleDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280
          }
        }}
      >
        <DrawerContent />
      </Drawer>
    </>
  );
};

export default ClinicalSupervisorHeader;
